import { useState, useEffect, useCallback } from "react";
import { X, FileDown, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const SESSION_KEY = "lead_magnet_shown";
const EXIT_INTENT_KEY = "exit_intent_shown";
const SHOW_DELAY_MS = 45_000;

const MARKETING_PATH_BLACKLIST = [
  "/dashboard",
  "/cctv-design",
  "/auth",
  "/settings",
];

// The guide we actually deliver. Promising "a PDF by email" with no send was a
// broken promise (lm_success used to say "check your email"); instead we hand
// over the existing on-site step-by-step guide immediately on success.
const GUIDE_HREF = "/blog/how-to-design-cctv-system";

export const LeadMagnetPopup = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !consent || loading) return;

      setLoading(true);

      try {
        const { error } = await supabase.from("leads").upsert(
          {
            email: email.trim().toLowerCase(),
            source: "lead_magnet",
            page_url: window.location.pathname,
          },
          { onConflict: "email" }
        );

        if (error) throw error;

        // Keep localStorage flag so popup won't show again
        localStorage.setItem("lead_email_captured", "1");

        // Success state delivers the guide on-site (no auto-close — let them
        // click through to the guide we actually promised).
        setSubmitted(true);
      } catch (err) {
        console.error("Failed to save lead:", err);
        toast.error(t("toast_generic_error", { defaultValue: "Something went wrong. Please try again." }));
      } finally {
        setLoading(false);
      }
    },
    [email, consent, loading, t]
  );

  useEffect(() => {
    // Don't show for authenticated users
    if (user) return;

    // Don't show if already shown this session
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Don't show if email was already captured
    if (localStorage.getItem("lead_email_captured")) return;

    // Don't show on non-marketing pages
    const path = window.location.pathname;
    if (MARKETING_PATH_BLACKLIST.some((p) => path.startsWith(p))) return;

    const timer = setTimeout(() => {
      // Check again at trigger time — exit intent may have fired in the meantime
      if (sessionStorage.getItem(EXIT_INTENT_KEY)) return;
      if (sessionStorage.getItem(SESSION_KEY)) return;

      sessionStorage.setItem(SESSION_KEY, "1");
      setVisible(true);
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user]);

  return (
    <>
      {visible && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
          onClick={handleClose}
        >
          <div
            className="relative w-full max-w-md rounded-none border border-neutral-200 dark:border-brand-border-strong bg-slate-950 p-8 shadow-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
              aria-label={t('common:close_popup', { defaultValue: 'Close popup' })}
            >
              <X className="w-5 h-5" />
            </button>

            {submitted ? (
              /* Success state */
              <div className="flex flex-col items-center py-4">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-600/20">
                  <CheckCircle2 className="h-7 w-7 text-green-400" />
                </div>
                <p className="text-center text-lg font-semibold text-white">
                  {t("lm_guide_ready", { defaultValue: "Your CCTV design guide is ready." })}
                </p>
                <a
                  href={GUIDE_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setVisible(false)}
                  className="mt-5 inline-block w-full bg-[var(--color-brand-blue)] hover:bg-[var(--color-brand-blue-hover)] text-white font-semibold py-2.5 text-center transition-colors"
                >
                  {t("lm_open_guide", { defaultValue: "Open the guide" })}
                </a>
              </div>
            ) : (
              /* Form state */
              <>
                {/* Icon */}
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-brand-blue)]/10">
                  <FileDown className="h-7 w-7 text-[var(--color-brand-blue)]" />
                </div>

                {/* Badge */}
                <div className="flex justify-center mb-3">
                  <span className="inline-block rounded-full bg-[var(--color-brand-blue)]/10 border border-[var(--color-brand-blue)]/30 px-3 py-0.5 text-xs font-semibold text-[var(--color-brand-blue)] uppercase tracking-wider">
                    {t("lm_badge")}
                  </span>
                </div>

                {/* Title */}
                <h2 className="text-center text-xl font-bold text-white sm:text-2xl">
                  {t("lm_title")}
                </h2>

                {/* Description */}
                <p className="mt-3 text-center text-sm text-gray-400 leading-relaxed">
                  {t("lm_desc")}
                </p>

                {/* Bullet points */}
                <ul className="mt-4 space-y-2 text-sm text-gray-300">
                  {(["lm_bullet1", "lm_bullet2", "lm_bullet3"] as const).map(
                    (key) => (
                      <li key={key} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-[var(--color-brand-blue)] mt-0.5 flex-shrink-0" />
                        <span>{t(key)}</span>
                      </li>
                    )
                  )}
                </ul>

                {/* Email form */}
                <form onSubmit={handleSubmit} className="mt-5">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("lm_email_placeholder")}
                    className="w-full rounded-none border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white placeholder-gray-500 outline-none focus:border-[var(--color-brand-blue)] focus:ring-1 focus:ring-[var(--color-brand-blue)]/20 transition-colors"
                  />
                  {/* Explicit, unchecked opt-in — EU-heavy traffic, GDPR. */}
                  <label className="mt-3 flex items-start gap-2 text-xs text-gray-400 leading-snug cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 flex-shrink-0 accent-[var(--color-brand-blue)]"
                    />
                    <span>
                      {t("lm_consent", { defaultValue: "I agree to receive occasional CCTV design tips by email. Unsubscribe anytime." })}
                    </span>
                  </label>
                  <Button
                    type="submit"
                    disabled={loading || !consent}
                    className="mt-3 w-full bg-[var(--color-brand-blue)] hover:bg-[var(--color-brand-blue-hover)] text-white font-semibold py-2.5"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t("lm_cta")
                    )}
                  </Button>
                </form>

                {/* No thanks */}
                <button
                  onClick={handleClose}
                  className="mt-3 block w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {t("lm_no_thanks")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
